import useHasAccess from "@hooks/useHasAccess"
import useIsDesktop from "@hooks/useIsDesktop"
import useSlugExtractor from "@hooks/useSlugExtractor"

import { AccessCheck } from "@particles"

import { NavTopBar, ObjectsView, ViewBar, ViewMenu } from "@organisms"
import CardsView from "@organisms/ObjectsView/CardsView/CardsView"
import TableView from "@organisms/ObjectsView/TableView/TableView"
import ViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider"

import { AppFrame } from "@templates"

import { INVOICES_ROUTES } from "@routes/invoices"

import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    INVOICES_LIST_CONTROLS_ACTIVE_TAB,
    INVOICES_LIST_CONTROLS_KEYWORD_SEARCH,
    INVOICES_LIST_CONTROLS_PHASE,
    INVOICES_LIST_CONTROLS_VIEW,
} from "@constants/storage"

import { INVOICE_CARDS_VIEW_ATTRIBUTE_OPTIONS, INVOICE_CARDS_VIEW_SORT_OPTIONS } from "./InvoiceList.constants"
import { InvoiceObjectsViewTabs } from "./InvoiceObjectsViewTabs"
import InvoiceCardsView from "./views/InvoiceCardsView/InvoiceCardsView"
import InvoiceTableView from "./views/InvoiceTableView/InvoiceTableView"

export default function InvoiceList() {
    const { hasPermission } = useHasAccess()
    const isDesktop = useIsDesktop()

    const [createRoute] = useSlugExtractor([INVOICES_ROUTES.CREATE])

    return (
        <AppFrame.Root collaborationRoomName="invoice-list">
            <ViewTypesProvider
                viewTypes={["table", "cards"]}
                viewTypeKey={INVOICES_LIST_CONTROLS_VIEW}
                defaultViewType={isDesktop ? "table" : "cards"}
            >
                {({ activeViewType }) => (
                    <>
                        <NavTopBar.Root>
                            <NavTopBar.Header>
                                <NavTopBar.NavTopBarHeaderStart>
                                    <NavTopBar.PageTitle label={objectMap.invoice.labelPlural} />
                                </NavTopBar.NavTopBarHeaderStart>
                                <NavTopBar.NavTopBarHeaderEnd>
                                    <NavTopBar.PagePresence />
                                    <AccessCheck
                                        permissions={{
                                            invoices_list_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <AccessCheck
                                            permissions={{
                                                invoices_view_permission: PERMISSION_LEVEL.FULL,
                                            }}
                                        >
                                            <NavTopBar.SearchButton scope="Invoices" />
                                        </AccessCheck>
                                    </AccessCheck>
                                    <AccessCheck
                                        permissions={{
                                            invoices_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <NavTopBar.CTAButton destination={createRoute} />
                                    </AccessCheck>
                                </NavTopBar.NavTopBarHeaderEnd>
                            </NavTopBar.Header>
                        </NavTopBar.Root>
                        <ObjectsView.Provider
                            activePhaseStorageKey={INVOICES_LIST_CONTROLS_PHASE}
                            activeTabStorageKey={INVOICES_LIST_CONTROLS_ACTIVE_TAB}
                            searchKeywordsStorageKey={INVOICES_LIST_CONTROLS_KEYWORD_SEARCH}
                            listTitle="Invoices"
                            objectType="invoice"
                            objectIndexName="Invoice"
                            objectName="Invoice"
                            defaultActivePhase="open"
                            objectCreateRoute={createRoute}
                            isRowClickable={hasPermission("invoices_view_permission", PERMISSION_LEVEL.FULL)}
                            isEmptyStateCreateButtonVisible={hasPermission(
                                "invoices_create_permission",
                                PERMISSION_LEVEL.RESTRICTED,
                            )}
                        >
                            <TableView.Provider>
                                <CardsView.Provider
                                    attributes={INVOICE_CARDS_VIEW_ATTRIBUTE_OPTIONS}
                                    sortOptions={INVOICE_CARDS_VIEW_SORT_OPTIONS}
                                >
                                    <ViewBar.Root>
                                        <ViewBar.Start>
                                            <ViewBar.Phases>
                                                <ObjectsView.Phase phase="open" title="Open" />
                                                <ObjectsView.Phase phase="closed" title="Closed" />
                                            </ViewBar.Phases>
                                            <ViewBar.VerticalDivider />
                                            <InvoiceObjectsViewTabs />
                                        </ViewBar.Start>
                                        <ViewBar.End>
                                            <ObjectsView.SearchInput />
                                            <ViewMenu />
                                        </ViewBar.End>
                                    </ViewBar.Root>
                                    {activeViewType === "table" && <InvoiceTableView />}
                                    {activeViewType === "cards" && <InvoiceCardsView />}
                                </CardsView.Provider>
                            </TableView.Provider>
                        </ObjectsView.Provider>
                    </>
                )}
            </ViewTypesProvider>
        </AppFrame.Root>
    )
}
